import type { SectionColor } from "../risikoanalyseStep/types";

/**
 * Feld- und Abschnittsmodell der Ertragsausfall-/BU-Vertragsmaske.
 *
 * Quelle ist die fachliche Felddefinition „Ertragsausfall Vertragsmaske
 * f. separate Deckung.xlsx“ (Blatt „Vertragsmaske“, Stand 26.08.2026). Deren
 * Spalten sind hier 1:1 abgebildet:
 *
 *   Spalte B „Feld“                     → `label`
 *   Spalte C „Inhalt / Auswahl“         → `type` + `options` / `parts`
 *   Spalte D „Datenquelle / DSI-Bezug“  → `source`
 *   Spalte E „Prüf-/Abgleichhinweis“    → `check`
 *   Spalte F „Pflicht / Logik“          → `requirement`
 */

/** Einfache Eingabetypen (ein Wert pro Feld). */
export type EaFieldType =
  | "yesno"
  | "text"
  | "textarea"
  | "eur"
  | "percent"
  | "months"
  | "number"
  | "date"
  | "select"
  | "tags"
  /** Zusammengesetztes Feld: mehrere Teilwerte in einer Zeile (Spalte C mit „;“ / „/“). */
  | "composite"
  /** Deckungsbaustein: Ja/Nein-Schalter, der Teilfelder aufklappt (Abschnitt 5). */
  | "coverage"
  /** Vom System berechnet — nicht editierbar. */
  | "computed"
  /** Dokumentverknüpfung (Platzhalter bis zur Dokumenten-Anbindung). */
  | "document";

/** Teilfeld eines `composite`- oder `coverage`-Feldes. */
export type EaFieldPart = {
  /** Suffix des Werteschlüssels: `<field.key>.<part.key>`. */
  key: string;
  label: string;
  type: Exclude<EaFieldType, "composite" | "coverage" | "computed">;
  options?: string[];
  placeholder?: string;
  /** Spaltenbreite im Teilfeld-Raster (1 = schmal, 2 = breit). */
  span?: 1 | 2;
};

export type EaField = {
  /** Schlüssel ohne Präfix; gespeichert wird als Characteristic `ea.<key>`. */
  key: string;
  /** Spalte B der Felddefinition. */
  label: string;
  type: EaFieldType;
  /** Auswahlwerte aus Spalte C (bei `select`). */
  options?: string[];
  /** Teilfelder bei `composite` / `coverage`. */
  parts?: EaFieldPart[];
  /** Spalte D — Datenquelle / DSI-Bezug. */
  source?: string;
  /** Spalte E — Prüf-/Abgleichhinweis. */
  check?: string;
  /** Spalte F — Pflicht / Logik. */
  requirement?: string;
  placeholder?: string;
  /** Zusatztext unter dem Feld (Einheit, Erläuterung). */
  hint?: string;
  /** Breite im Feldraster: 1 = halbe Zeile, 2 = ganze Zeile. */
  span?: 1 | 2;
  /** Nur anzeigen, wenn die Bedingung auf den bisherigen Werten zutrifft. */
  showWhen?: (values: EaValues) => boolean;
};

/** Alle Feldwerte flach: Schlüssel → String (auch Zahlen werden als String gehalten). */
export type EaValues = Record<string, string>;

/** KBU-Status einer Gefahr laut DSI (Spalte C in Abschnitt 4). */
export type KbuStatus = "0" | "1" | "2" | "";

/** Eine Gefahrenzeile aus Abschnitt 4 „Versicherte Gefahren – KBU-Status“. */
export type EaGefahr = {
  /** Werteschlüssel, entspricht dem DSI-Feld in Kleinschreibung. */
  key: string;
  label: string;
  /** DSI-Feldname (Spalte D), z. B. `FEUER_O`. */
  dsi: string;
};

export type EaStepKind =
  | "grunddaten"
  | "umfeld"
  | "bemessung"
  | "limits"
  | "haftzeit"
  | "gefahren"
  | "deckungen"
  | "klauseln"
  | "dokumentation"
  | "sollwerte"
  | "ergebnis";

export type EaStepDef = {
  kind: EaStepKind;
  key: string;
  title: string;
  description?: string;
  /** Feldliste dieses Schritts (leer bei Schritten mit eigener Darstellung). */
  fields?: EaField[];
};

export type EaSectionDef = {
  key: string;
  /** Abschnittstitel wie in der Excel („1. Grunddaten / Vertragsart“ ohne Nummer). */
  title: string;
  /** Kurzform für die Seitenleiste. */
  shortTitle: string;
  color: SectionColor;
  steps: EaStepDef[];
};

/** Kontaktkopf für die Kontextleiste. */
export type ContactSummary = {
  id: number;
  displayName: string;
  street?: string;
  postalCode?: string;
  city?: string;
  country?: string;
};

export type RiskOption = {
  id: number;
  name: string;
  riskType?: string;
};

export type EaAnswers = {
  contact: ContactSummary | null;
  /** Ausgewählte Betriebsstätten / Risikoorte (Abschnitt 1). */
  betriebsstaetten: number[];
  /** KBU-Status je Gefahr (Abschnitt 4). */
  kbu: Record<string, KbuStatus>;
  /** Alle übrigen Feldwerte. */
  values: EaValues;
};

export const emptyAnswers = (): EaAnswers => ({
  contact: null,
  betriebsstaetten: [],
  kbu: {},
  values: {},
});
